// Market health is classified by the backend. The browser only presents the
// accepted observation; it never counts failures or decides a security is dead.
export const MARKET_HEALTH_CHANGED_EVENT = 'alphamizer:market-health-changed';

const STATUSES = new Set(['healthy', 'concern', 'sick', 'dead', 'unknown']);

export function marketHealthStatus(health) {
    return STATUSES.has(health?.status) ? health.status : 'unknown';
}

export const MARKET_HEALTH_LABELS = {
    healthy: 'Healthy',
    concern: 'Concern',
    sick: 'Sick',
    dead: 'Dead',
    unknown: 'Unknown',
};

// Sick or Dead market data tints the whole Watchlist row; every other status
// shows only its icon.
export function marketHealthDistressed(health) {
    const status = marketHealthStatus(health);
    return status === 'sick' || status === 'dead';
}

const SEVERITY = { healthy: 0, unknown: 1, concern: 2, sick: 3, dead: 4 };

/** The health column's sort key: higher is more severe. */
export function marketHealthSeverity(health) {
    return SEVERITY[marketHealthStatus(health)];
}

const REASON_LABELS = {
    beta_unavailable: 'Beta is unavailable',
    adtv_unavailable: 'ADTV is unavailable',
    closing_price_unavailable: 'The latest closing price is unavailable',
    market_calendar_unavailable: 'The market calendar could not be verified',
    mapping_unavailable: 'A verified market-data symbol is unavailable',
    mapping_repair_available: 'A replacement market-data symbol is available for review',
    mapping_review_required: 'The market-data symbol needs review',
    provider_unavailable: 'The market-data provider is temporarily unavailable',
    check_overdue: 'The next health check is overdue',
    not_checked: 'Waiting for the first health check',
    health_not_checked: 'Waiting for the first health check',
};

export function marketHealthReasonLabel(reason) {
    const text = String(reason || '').trim();
    if (REASON_LABELS[text]) return REASON_LABELS[text];
    const words = text.replace(/[_-]+/g, ' ');
    return words ? words[0].toUpperCase() + words.slice(1) : '';
}

// Health observations advance independently of prices. An older quote may
// contain a newer health observation, and an older health read must not erase
// a newer observation already present in the published Watchlist rows.
export function latestMarketHealth(current, incoming) {
    if (!incoming || typeof incoming !== 'object') return current;
    if (!current || typeof current !== 'object') return incoming;
    if (current.target_fingerprint && incoming.target_fingerprint
        && current.target_fingerprint !== incoming.target_fingerprint) return current;
    const currentChecked = Date.parse(current.last_checked_at);
    const incomingChecked = Date.parse(incoming.last_checked_at);
    if (Number.isFinite(currentChecked) && Number.isFinite(incomingChecked)) {
        if (incomingChecked < currentChecked) return current;
        if (incomingChecked > currentChecked) return incoming;
    }
    const currentVersion = Number(current.health_version);
    const incomingVersion = Number(incoming.health_version);
    if (current.health_version != null && incoming.health_version != null
        && Number.isSafeInteger(currentVersion) && Number.isSafeInteger(incomingVersion)
        && incomingVersion < currentVersion) return current;
    // On equal observations the latest server projection wins, including an
    // unknown projection for an overdue check. Freshness is never inferred here.
    return incoming;
}
